import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  EMPTY_USER,
  getConfig,
  isValidUserType,
  buildRegisterPayload,
  isDriverUserType,
  type FormState,
  type StepId,
  type UserType,
  type RegisterReference,
} from '@/shared/types/users.types';
import { colorHelpers } from '@/utils/colorHelpers';
import { register, saveSession } from '@/domains/auth/services/auth.service';
import { fetchRegisterReference } from '@/domains/auth/services/reference.service';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { StepContact } from './steps/StepContact';
import { StepIdentity } from './steps/StepIdentity';
import { StepVehicle } from './steps/StepVehicle';
import { StepAvailability } from './steps/StepAvailability';
import { StepDocuments } from './steps/StepDocuments';
import { StepConsents } from './steps/StepConsents';
import { InvalidType } from './steps/InvalidType';
import { ProgressBar } from '@/components/common/ProgressBar';
import { OtherTypes } from './steps/OtherTypes';

export default function UniversalRegisterForm() {
  const { userType = '' } = useParams<{ userType: string }>();
  const navigate = useNavigate();

  if (!isValidUserType(userType)) return <InvalidType userType={userType} />;

  const config = getConfig(userType)!;
  const { steps, color, label, icon, description } = config;
  const c = colorHelpers(color);

  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY_USER);
  const [reference, setReference] = useState<RegisterReference | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingRef, setLoadingRef] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRegisterReference()
      .then(setReference)
      .catch(() => setError('Impossible de charger les données d\'inscription.'))
      .finally(() => setLoadingRef(false));
  }, []);

  const currentStep = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const toggleSpec = (id: number) =>
    setForm(prev => ({
      ...prev,
      specializationIds: prev.specializationIds.includes(id)
        ? prev.specializationIds.filter(x => x !== id)
        : [...prev.specializationIds, id],
    }));

  const toggleSlot = (id: number) =>
    setForm(prev => ({
      ...prev,
      availabilitySlotIds: prev.availabilitySlotIds.includes(id)
        ? prev.availabilitySlotIds.filter(x => x !== id)
        : [...prev.availabilitySlotIds, id],
    }));

  const toggleZone = (id: number) =>
    setForm(prev => ({
      ...prev,
      coverageZoneIds: prev.coverageZoneIds.includes(id)
        ? prev.coverageZoneIds.filter(x => x !== id)
        : [...prev.coverageZoneIds, id],
    }));

  const setDoc = (key: string, file: File | null) =>
    setForm(prev => ({ ...prev, documents: { ...prev.documents, [key]: file } }));

  const validate = (): string | null => {
    switch (currentStep.id as StepId) {
      case 'contact': {
        if (!form.firstName.trim()) return 'Le prénom est requis.';
        if (!form.lastName.trim()) return 'Le nom est requis.';
        if (!form.phone.trim()) return 'Le numéro de téléphone est requis.';
        if (config.emailRequired && !form.email.trim()) return "L'adresse e-mail est requise.";
        if (form.email && !/\S+@\S+\.\S+/.test(form.email)) return 'Adresse e-mail invalide.';
        if (!form.password) return 'Le mot de passe est requis.';
        if (form.password.length < 8) return 'Mot de passe : 8 caractères minimum.';
        if (form.password !== form.confirmPassword) return 'Les mots de passe ne correspondent pas.';
        return null;
      }
      case 'identity':
        if (!form.zoneId) return 'Veuillez sélectionner une zone.';
        return null;
      case 'vehicle':
        if (!form.vehicleTypeId) return 'Veuillez sélectionner un type de véhicule.';
        if (!form.capacityWeightKg || !form.capacityVolumeM3) return 'Capacité poids et volume requises.';
        if (form.specializationIds.length === 0) return 'Sélectionnez au moins une spécialisation.';
        return null;
      case 'availability':
        if (form.availabilitySlotIds.length === 0) return 'Sélectionnez au moins un créneau.';
        if (form.coverageZoneIds.length === 0) return 'Sélectionnez au moins une zone de couverture.';
        return null;
      case 'documents':
        if (userType === 'livreur-pro') {
          const hasDoc = Object.values(form.documents).some(Boolean);
          if (!hasDoc) return 'Au moins un document est obligatoire pour un livreur professionnel.';
        }
        return null;
      case 'consents': {
        if (!form.acceptTerms) return 'Veuillez accepter les conditions générales.';
        if (!form.acceptPrivacy) return 'Veuillez accepter la politique de confidentialité.';
        if (isDriverUserType(userType as UserType)) {
          if (!form.acceptQuality) return 'Veuillez vous engager sur la qualité de service.';
          if (!form.acceptVerification) return 'Veuillez autoriser la vérification de vos documents.';
        }
        return null;
      }
      default:
        return null;
    }
  };

  const handleNext = () => {
    setError('');
    const err = validate();
    if (err) { setError(err); return; }
    if (!isLast) setStepIdx(i => i + 1);
  };

  const handlePrev = () => {
    setError('');
    if (stepIdx > 0) setStepIdx(i => i - 1);
  };

  const handleSubmit = async () => {
    setError('');
    const err = validate();
    if (err) { setError(err); return; }
    if (!reference) {
      setError('Données de référence indisponibles.');
      return;
    }

    setLoading(true);

    try {
      const payload = buildRegisterPayload(form, userType as UserType, reference);
      const data = await register(payload);

      if (data.token) {
        const session = saveSession(data);
        navigate(session.dashboardPath);
      } else {
        navigate('/login?registered=1');
      }
    } catch (e: unknown) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // State de chargement épuré
  if (loadingRef || !reference) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-teal-500 animate-spin mb-3 block" />
          <p className="text-slate-400 text-xs font-medium tracking-wide">Préparation de votre espace d'inscription…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col antialiased">

      {/* ── HEADER PREMIUM GLOBAL ─────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm px-4 h-16 flex items-center">
        <div className="max-w-5xl w-full mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-9 h-9 bg-teal-500 rounded-xl flex items-center justify-center text-slate-950 font-black text-lg shadow-md shadow-teal-500/20 transition-transform group-hover:scale-105">
              O
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-base tracking-tight text-slate-900 leading-none">ONAPILA</span>
              <span className="text-[9px] text-teal-600 font-medium tracking-widest uppercase mt-0.5">Livrer easy</span>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <Link to="/" className="text-xs font-semibold text-slate-500 hover:text-slate-900 px-3 py-2 rounded-xl hover:bg-slate-50 transition-all">
              Accueil
            </Link>
            <div className="h-4 w-px bg-slate-200" />
            <Link to="/login" className="text-xs font-bold text-teal-600 hover:text-teal-700 px-3 py-2 rounded-xl bg-teal-50 hover:bg-teal-100/70 transition-all">
              Déjà inscrit ?
            </Link>
          </div>
        </div>
      </header>

      {/* ── ZONE DE CONTENU PRINCIPALE ─────────────────────────────────────────── */}
      <div className="flex-1 max-w-2xl w-full mx-auto px-4 py-10 md:py-14">

        {/* En-tête de profil dynamique */}
        <div className="text-center mb-8">
          <div className={`w-16 h-16 bg-${color}-500/10 border border-${color}-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-${color}-600`}>
            <i className={`${icon} text-2xl`} />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">
            Inscription — {label}
          </h1>
          <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Barre de progression épurée */}
        <div className="mb-8 bg-white p-4 rounded-xl border border-slate-100 shadow-sm shadow-slate-200/50">
          <ProgressBar steps={steps} current={stepIdx} color={color} />
        </div>

        {/* CONTENEUR DU FORMULAIRE UNIQUE */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/40 p-6 md:p-8">

          {error && (
            <div className="bg-rose-50 border border-rose-100 text-rose-800 p-3.5 rounded-xl mb-6 text-xs font-medium flex items-start gap-2.5 animate-fadeIn">
              <i className="ri-error-warning-fill text-rose-500 text-base shrink-0" />
              <span className="leading-normal">{error}</span>
            </div>
          )}

          {/* Render des étapes injectées */}
          <div className="min-h-[220px]">
            {currentStep.id === 'contact' && (
              <StepContact form={form} set={set} config={config} c={c} />
            )}
            {currentStep.id === 'identity' && (
              <StepIdentity form={form} set={set} reference={reference} c={c} />
            )}
            {currentStep.id === 'vehicle' && (
              <StepVehicle form={form} set={set} toggleSpec={toggleSpec} reference={reference} c={c} />
            )}
            {currentStep.id === 'availability' && (
              <StepAvailability
                form={form}
                toggleSlot={toggleSlot}
                toggleZone={toggleZone}
                reference={reference}
                c={c}
              />
            )}
            {currentStep.id === 'documents' && (
              <StepDocuments form={form} setDoc={setDoc} userType={userType} reference={reference} c={c} />
            )}
            {currentStep.id === 'consents' && (
              <StepConsents form={form} set={set} config={config} userType={userType} />
            )}
          </div>

          {/* ACTIONS DE NAVIGATION / ACTIONS BAR */}
          <div className="flex justify-between pt-6 border-t border-slate-100 mt-8">
            <button
              onClick={handlePrev}
              disabled={stepIdx === 0 || loading}
              className="px-5 py-2.5 text-xs font-bold rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-s-line text-sm" />
              Précédent
            </button>

            {isLast ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2.5 text-xs font-bold tracking-wider uppercase text-white rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5 ${c.bg} ${c.bgHov}`}
              >
                {loading ? (
                  <><i className="ri-loader-4-line text-sm animate-spin" /> Création…</>
                ) : (
                  <><i className="ri-check-double-line text-sm" /> Finaliser l'inscription</>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={loading}
                className={`px-6 py-2.5 text-xs font-bold tracking-wider uppercase text-white rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5 ${c.bg} ${c.bgHov}`}
              >
                Suivant
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Section alternative d'inscription en bas */}
        <div className="mt-6">
          <OtherTypes current={userType} />
        </div>
      </div>
    </div>
  );
}